import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { gaEvent } from '../../../utils/google-analytics';
import { find } from 'lodash';
import {
  BalancesDataContainer,
  BalancesHeaderContainer,
  CurrentDueBalancesContainer,
  CurrentDueBalancesData,
  CurrentDueBalancesHeader,
  CurrentDueBalancesHeaderMobile,
  CurrentPayPlanHeader,
  DueDateBalancesContainer,
  DueDateBalancesHeader,
  DueDateBalancesHeaderMobile,
  HidePaymentArea,
  NewPaymentActionContainer,
  PaymentScheduleButton,
  PaymentScheduleHideButton,
  PayoffAmountBalancesContainer,
  PayoffAmountBalancesData,
  PayoffAmountBalancesHeader,
  PayoffAmountBalancesHeaderMobile,
  PayPlanScheduleContainer,
  PolicyNumberBalancesHeader,
  PolicyNumberContainer,
  PolicyNumberData,
  PolicyNumberHeaderMobile,
  ShowPaymentContainer,
} from './styles';
import { checkEditable, collapseEventBody, expandEventBody } from './utils';
import EditPaySchedule from './editPaySchedule';
import EditPayMethod from './editPayMethod';
import PayScheduleTable from './payScheduleTable';
import { resetStepTwoState } from '../make-payment/step-two-amount/makePaymentStepTwoReducer';
import { getPayPlanOptions } from './policyAPI';
import { setNewPlanSchedule } from './accountDetailsReducer';
import CancelSaveUnderlyingAccountsContainer from './cancelSaveUnderlyingAccountsContainer';
import { ga4Event } from '../../../utils/google-analytics-4';
import { useSnackbar } from 'notistack';
import { PayPlanMethodInfo } from './account-row-components/payPlanMethodInfo';
import ChangePaySchedule from './account-row-components/changePaySchedule';
import ChangePayMethod from './account-row-components/changePayMethod';

export const UnderlyingAccountRows = (props) => {
  const {
    dispatch,
    editPayPlan,
    fullAccount,
    policies,
    payPlanTypesToFilter,
    isUnderlyingPolicies,
    underlyingPoliciesPayPlansEditable,
    initialState,
    policyToUpdatePayPlan,
    setPolicyToUpdatePayPlan,
    showPaymentSchedule,
    setShowPaymentSchedule,
    showEditPaymentSchedule,
    setShowEditPaymentSchedule,
    showEditPaymentMethod,
    setShowEditPaymentMethod,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  // Statement account should be excluded from "Balances" section, only the underlying policy account should be displayed.
  let policyAccount = fullAccount.underlyingBillingAccounts
    ? fullAccount.underlyingBillingAccounts.filter(
        (account) => account.accountDisplayName !== 'Statement',
      )
    : [fullAccount];

  const getPayPlanOptionsAndShowSchedule = (account) => {
    dispatch(setNewPlanSchedule(null));
    dispatch(resetStepTwoState());
    setShowPaymentSchedule({
      ...initialState,
      [account.id]: !showPaymentSchedule[account.id],
    });

    gaEvent(expandEventBody);
    ga4Event(expandEventBody);
    const temp = find(policies, ['policyNumber', account.accountNumber]);
    setPolicyToUpdatePayPlan(temp);
    dispatch(
      getPayPlanOptions(
        temp?.policyRef ? temp?.policyRef : account?.arPolicy?.policyRef,
        payPlanTypesToFilter,
        enqueueSnackbar,
      ),
    );
  };

  return (
    <>
      <BalancesHeaderContainer>
        <PolicyNumberBalancesHeader>Policy Number</PolicyNumberBalancesHeader>
        <PayoffAmountBalancesHeader>Payoff Amount</PayoffAmountBalancesHeader>
        <CurrentDueBalancesHeader>Current Due</CurrentDueBalancesHeader>
        <DueDateBalancesHeader>Payment Schedule</DueDateBalancesHeader>
      </BalancesHeaderContainer>
      {policyAccount &&
        policyAccount.map((account) => {
          return (
            <BalancesDataContainer key={account.id}>
              <PolicyNumberContainer>
                <PolicyNumberHeaderMobile>Policy Number</PolicyNumberHeaderMobile>
                <PolicyNumberData>{account.accountNumber}</PolicyNumberData>
              </PolicyNumberContainer>
              <PayoffAmountBalancesContainer>
                <PayoffAmountBalancesHeaderMobile>Payoff Amount</PayoffAmountBalancesHeaderMobile>
                <PayoffAmountBalancesData>
                  ${parseFloat(account.totalAmt).toFixed(2)}
                </PayoffAmountBalancesData>
              </PayoffAmountBalancesContainer>
              <CurrentDueBalancesContainer>
                <CurrentDueBalancesHeaderMobile>Current Due</CurrentDueBalancesHeaderMobile>
                <CurrentDueBalancesData>
                  ${parseFloat(account.openAmt).toFixed(2)}
                </CurrentDueBalancesData>
              </CurrentDueBalancesContainer>
              <DueDateBalancesContainer>
                <DueDateBalancesHeaderMobile>Payment Schedule</DueDateBalancesHeaderMobile>
                {account.payPlan.schedule && showPaymentSchedule[account.id] === false ? (
                  <ShowPaymentContainer
                    onClick={() => {
                      getPayPlanOptionsAndShowSchedule(account);
                    }}
                    onKeyDown={(e) =>
                      e.key === 'Enter' ? getPayPlanOptionsAndShowSchedule(account) : null
                    }
                  >
                    <KeyboardArrowRightIcon />
                    <PaymentScheduleButton
                      aria-label={`show/hide payment plan for ${account.accountDisplayNumber}`}
                      $accountNumber={account.accountDisplayNumber}
                    >
                      Show
                      {editPayPlan && underlyingPoliciesPayPlansEditable ? `/Manage` : null} Payment
                      Schedule
                    </PaymentScheduleButton>
                  </ShowPaymentContainer>
                ) : (
                  <HidePaymentArea
                    onClick={() => {
                      gaEvent(collapseEventBody);
                      ga4Event(collapseEventBody);
                      setShowPaymentSchedule({
                        ...showPaymentSchedule,
                        [account.id]: !showPaymentSchedule[account.id],
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        ga4Event(collapseEventBody);
                        setShowPaymentSchedule({
                          ...showPaymentSchedule,
                          [account.id]: !showPaymentSchedule[account.id],
                        });
                      }
                    }}
                  >
                    <KeyboardArrowDownIcon />
                    <PaymentScheduleHideButton>Hide Payment Schedule</PaymentScheduleHideButton>
                  </HidePaymentArea>
                )}
              </DueDateBalancesContainer>

              {showPaymentSchedule[account.id] === true ? (
                <PayPlanScheduleContainer>
                  <CurrentPayPlanHeader>{account.payPlan.description}</CurrentPayPlanHeader>
                  <PayScheduleTable schedule={account.payPlan.schedule} />
                  <PayPlanMethodInfo account={account} />
                  {checkEditable(
                    account,
                    isUnderlyingPolicies,
                    underlyingPoliciesPayPlansEditable,
                    editPayPlan,
                  ) && (
                    <>
                      {!account.payPlan.payPlanTypeCd.includes('None') &&
                        !account.statementAccountRef && (
                          <ChangePayMethod
                            account={account}
                            setShowEditPaymentSchedule={setShowEditPaymentSchedule}
                            setShowEditPaymentMethod={setShowEditPaymentMethod}
                            showEditPaymentMethod={showEditPaymentMethod}
                            initialState={initialState}
                          />
                        )}
                      <ChangePaySchedule
                        account={account}
                        initialState={initialState}
                        showEditPaymentSchedule={showEditPaymentSchedule}
                        setShowEditPaymentSchedule={setShowEditPaymentSchedule}
                        setShowEditPaymentMethod={setShowEditPaymentMethod}
                      />
                      {showEditPaymentSchedule[account.id] === true ? (
                        <EditPaySchedule account={account} />
                      ) : null}
                      {showEditPaymentMethod[account.id] === true &&
                      !account.payPlan.payPlanTypeCd.includes('None') ? (
                        <EditPayMethod account={account} />
                      ) : null}{' '}
                      {/* direct accounts can edit just the payment method. Automated must change full pay schedule */}
                      <NewPaymentActionContainer>
                        {showEditPaymentSchedule[account.id] === true ||
                        showEditPaymentMethod[account.id] === true ? (
                          <CancelSaveUnderlyingAccountsContainer
                            showEditPaymentMethod={showEditPaymentMethod}
                            setShowPaymentSchedule={setShowPaymentSchedule}
                            showPaymentSchedule={showPaymentSchedule}
                            setShowEditPaymentMethod={setShowEditPaymentMethod}
                            setShowEditPaymentSchedule={setShowEditPaymentSchedule}
                            account={account}
                            fullAccount={fullAccount}
                            showEditPaymentSchedule={showEditPaymentSchedule}
                            policyToUpdatePayPlan={policyToUpdatePayPlan}
                            setPolicyToUpdatePayPlan={setPolicyToUpdatePayPlan}
                          />
                        ) : null}
                      </NewPaymentActionContainer>
                    </>
                  )}
                </PayPlanScheduleContainer>
              ) : null}
            </BalancesDataContainer>
          );
        })}
    </>
  );
};
UnderlyingAccountRows.propTypes = {
  fullAccount: PropTypes.shape({
    id: PropTypes.string,
    underlyingBillingAccounts: PropTypes.array,
    accountDisplayName: PropTypes.string,
    accountNumber: PropTypes.string,
    totalAmt: PropTypes.string,
    openAmt: PropTypes.string,
    payPlan: PropTypes.shape({
      schedule: PropTypes.array,
    }),
  }),
  dispatch: PropTypes.any,
  editPayPlan: PropTypes.bool,
  dueDate: PropTypes.string,
  newPlan: PropTypes.any,
  payPlanDateToPay: PropTypes.any,
  policies: PropTypes.any,
  payPlanTypesToFilter: PropTypes.any,
  underlyingPoliciesPayPlansEditable: PropTypes.bool,
  isUnderlyingPolicies: PropTypes.bool,
  initialState: PropTypes.any,
  policyToUpdatePayPlan: PropTypes.any,
  setPolicyToUpdatePayPlan: PropTypes.any,
  showPaymentSchedule: PropTypes.any,
  setShowPaymentSchedule: PropTypes.any,
  showEditPaymentSchedule: PropTypes.any,
  setShowEditPaymentSchedule: PropTypes.any,
  showEditPaymentMethod: PropTypes.any,
  setShowEditPaymentMethod: PropTypes.any,
};
const mapStateToProps = (state) => ({
  editPayPlan: state.configurationSlice.features.features.editPayPlan,
  policies: state.policySlice.policies,
  payPlanTypesToFilter: state.configurationSlice.features.features.payPlanTypesToFilter,
  underlyingPoliciesPayPlansEditable:
    state.configurationSlice.features.features.underlyingPoliciesPayPlansEditable,
});
export default connect(mapStateToProps)(UnderlyingAccountRows);
